/**
 * Utilidades para el corte semanal de la farmacia.
 *
 * La semana operativa corre de VIERNES 00:00 → JUEVES 23:59:59.999.
 * El corte se hace al final del jueves (equivalente al inicio del viernes).
 *
 * Ejemplos:
 *   • Miércoles 8-jul-2026 → semana [vie 3-jul, jue 9-jul]
 *   • Viernes 10-jul-2026 00:15 → semana [vie 10-jul, jue 16-jul]
 *   • Jueves 9-jul-2026 23:59 → semana [vie 3-jul, jue 9-jul]
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Viernes que abre la semana operativa a la que pertenece `date`. */
export function weekStart(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // getDay(): 0=Domingo … 5=Viernes … 6=Sábado.
  const dow = d.getDay();
  // Días hacia atrás hasta el último viernes (viernes → 0, sábado → 1, ..., jueves → 6).
  const offset = (dow - 5 + 7) % 7;
  d.setDate(d.getDate() - offset);
  return d;
}

/** Jueves 23:59:59.999 que cierra la semana operativa de `date`. */
export function weekEnd(date = new Date()) {
  const s = weekStart(date);
  const e = new Date(s.getTime() + 7 * DAY_MS - 1);
  return e;
}

/** ¿La venta cae dentro de la semana operativa actual? */
export function isInCurrentWeek(iso, now = new Date()) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= weekStart(now).getTime() && t <= weekEnd(now).getTime();
}

/** Formatea una semana como "6 – 12 sep 2026". */
export function formatWeekRange(startDate) {
  const start = weekStart(startDate);
  const end = new Date(start.getTime() + 6 * DAY_MS);
  const fmtDay = (d) => d.toLocaleDateString("es-ES", { day: "numeric" });
  const fmtMonthYear = (d) =>
    d.toLocaleDateString("es-ES", { month: "short", year: "numeric" });
  if (start.getMonth() === end.getMonth()) {
    return `${fmtDay(start)} – ${fmtDay(end)} ${fmtMonthYear(end)}`;
  }
  const fmtShort = (d) =>
    d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
  return `${fmtShort(start)} – ${fmtShort(end)} ${end.getFullYear()}`;
}

/**
 * Agrupa una lista de ventas por semana operativa. Devuelve un arreglo
 * ordenado descendente (semana más reciente primero) con:
 *   { start, end, sales, tot, com, pending, allPaid }
 */
export function groupSalesByWeek(sales) {
  const map = new Map();
  for (const s of sales) {
    const start = weekStart(s.created_at);
    const key = start.getTime();
    if (!map.has(key)) {
      map.set(key, {
        start,
        end: weekEnd(s.created_at),
        sales: [],
        tot: 0,
        com: 0,
        pending: 0,
      });
    }
    const bucket = map.get(key);
    bucket.sales.push(s);
    bucket.tot += s.total_amount || 0;
    bucket.com += s.platform_commission_amount || 0;
    if (!s.pharmacy_paid) bucket.pending += s.platform_commission_amount || 0;
  }
  const rows = Array.from(map.values());
  rows.sort((a, b) => b.start.getTime() - a.start.getTime());
  for (const r of rows) r.allPaid = r.pending === 0;
  return rows;
}
